// Type definitions for native interfaces
declare global {
  interface Window {
    AndroidInterface?: {
      handleResponse: (responseId: string) => void;
    };
    webkit?: {
      messageHandlers: {
        iOSInterface: {
          postMessage: (message: unknown) => void;
        };
      };
    };
  }
}

// Message structure for iframe communication
export interface IframeMessage {
  source: "flight-recorder-web";
  type: "nativeResponse" | "ready" | "error" | "version";
  data: unknown;
  timestamp: number;
}

/**
 * Detects the current environment (Android, iOS, iframe, or web)
 */
export const detectEnvironment = (): "android" | "ios" | "iframe" | "web" => {
  if (
    window.AndroidInterface &&
    typeof window.AndroidInterface.handleResponse === "function"
  ) {
    return "android";
  }
  if (window.webkit?.messageHandlers?.iOSInterface) {
    return "ios";
  }
  if (window !== window.parent) {
    return "iframe";
  }
  return "web";
};

/**
 * Send a message to iframe parent window
 */
export const sendToIframeParent = (
  type: IframeMessage["type"],
  data: unknown,
  targetOrigin: string = "*",
): void => {
  if (window === window.parent) {
    console.warn("Not running in iframe, cannot send to parent");
    return;
  }

  const message: IframeMessage = {
    source: "flight-recorder-web",
    type,
    data,
    timestamp: Date.now(),
  };

  window.parent.postMessage(message, targetOrigin);
  console.log(`Sent ${type} message to iframe parent:`, data);
};

/**
 * Listen for messages from iframe parent
 */
export const listenToIframeParent = (
  callback: (message: MessageEvent) => void,
): (() => void) => {
  const handleMessage = (event: MessageEvent) => {
    // Optional: Add origin validation here for security
    // if (event.origin !== "https://your-app-shell-domain.com") return;

    callback(event);
  };

  window.addEventListener("message", handleMessage);

  // Return cleanup function
  return () => {
    window.removeEventListener("message", handleMessage);
  };
};

/**
 * Main function to send data to native app (Android, iOS, or iframe parent)
 */
/**
 * Send version information to parent (useful for iframe integration)
 */
export const sendVersionToParent = async (): Promise<void> => {
  try {
    const versionResponse = await fetch("/version.json");
    const versionData = await versionResponse.json();
    sendToIframeParent("version", versionData);
  } catch (error) {
    console.error("Failed to send version to parent:", error);
    sendToIframeParent("error", { message: "Failed to load version" });
  }
};

/**
 * Notify parent that the microfrontend is ready
 */
export const notifyParentReady = (): void => {
  const environment = detectEnvironment();
  if (environment === "iframe") {
    sendToIframeParent("ready", {
      microfrontend: "flight-recorder-web",
      environment,
      readyAt: new Date().toISOString(),
    });
  }
};

/**
 * Main function to send data to native app (Android, iOS, or iframe parent)
 */
export const sendToNativeApp = (data: string): void => {
  try {
    const environment = detectEnvironment();

    switch (environment) {
      case "android":
        window.AndroidInterface!.handleResponse(data);
        console.log("Sent response to Android:", data);
        break;

      case "ios":
        window.webkit!.messageHandlers.iOSInterface.postMessage(data);
        console.log("Sent response to iOS:", data);
        break;

      case "iframe":
        sendToIframeParent("nativeResponse", data);
        break;

      case "web":
      default:
        console.log("No native interface found, running in web browser");
        console.log("Data:", data);
        break;
    }
  } catch (error) {
    console.error("Error sending response to native app:", error);
  }
};
